import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import platformAPI from "../../../services/platformApi";
import { formatRub, MetricCard } from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const STATUS_LABEL = {
  confirmed: "Оплачен",
  pending: "Ожидает",
  rejected: "Отклонён",
  cancelled: "Отменён",
};

function formatDay(iso) {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

export default function PlatformPayments() {
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState("all");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params =
      filter === "all" ? { limit: 100 } : { limit: 100, status: filter };
    Promise.all([
      platformAPI.getPaymentsStats(),
      platformAPI.listPayments(params),
    ])
      .then(([st, list]) => {
        if (cancelled) return;
        setStats(st.stats || null);
        setPayments(list.payments || []);
        setErr("");
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e.response?.data?.error || "Не удалось загрузить оплаты");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const chartData = useMemo(
    () =>
      (stats?.revenue_daily || []).map((p) => ({
        name: formatDay(p.day),
        amount: p.amount,
      })),
    [stats?.revenue_daily],
  );

  return (
    <div className={s.page}>
      <PlatformPageHeader
        backTo="/admin/more"
        backLabel="Ещё"
        eyebrow="Финансы платформы"
        title="Оплаты клубов"
        lead="Подтверждённые платежи за SaaS-тарифы и статусы сессий оплаты."
        actions={
          <Link to="/admin/plans" className={s.heroActionBtn}>
            Тарифы
          </Link>
        }
      />

      {err ? <div className={s.alertError}>{err}</div> : null}
      {loading ? <div className={s.loading}>Загрузка…</div> : null}

      {!loading && stats ? (
        <>
          <section className={s.metricGrid}>
            <MetricCard
              label="Выручка за 30 дн."
              value={`${formatRub(stats.revenue_30d_rub)} ₽`}
              hint={`За 7 дн.: ${formatRub(stats.revenue_7d_rub)} ₽`}
              accent
            />
            <MetricCard
              label="Всего оплачено"
              value={`${formatRub(stats.revenue_total_rub)} ₽`}
              hint={`Платежей: ${stats.payments_confirmed_total || 0}`}
            />
            <MetricCard
              label="Ожидают оплаты"
              value={stats.payments_pending || 0}
              hint="Открытые сессии"
            />
            <MetricCard
              label="Отменены / отклонены"
              value={stats.payments_failed || 0}
              hint="За всё время"
            />
          </section>

          <section className={`${s.chartPanel} ${s.mobileChartPanel}`}>
            <h3 className={s.panelTitle}>Выручка по дням (30 дн.)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="payArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0f766e" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#0f766e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={4} />
                <YAxis tick={{ fontSize: 10 }} width={44} />
                <Tooltip formatter={(v) => [`${formatRub(v)} ₽`, "Выручка"]} />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#0f766e"
                  fill="url(#payArea)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </section>

          <section className={s.chartPanel}>
            <div className={s.panelHeaderRow}>
              <h3 className={s.panelTitle}>История платежей</h3>
            </div>
            <div className={s.tabRowScroll}>
              {[
                ["all", "Все"],
                ["confirmed", "Оплачены"],
                ["pending", "Ожидают"],
                ["cancelled", "Отменены"],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`${s.tabBtn} ${filter === id ? s.tabBtnActive : ""}`}
                  onClick={() => setFilter(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            {!payments.length ? (
              <p className={s.emptyHint}>Нет платежей по выбранному фильтру</p>
            ) : (
              <>
                <div className={s.mobileCardList}>
                  {payments.map((p) => (
                    <div key={p.id} className={s.mobileDataCard}>
                      <div className={s.mobileDataCardTop}>
                        {p.tenant_id ? (
                          <Link to={`/admin/tenants/${p.tenant_id}`}>
                            <strong>
                              {p.tenant_name || p.tenant_subdomain || "Клуб"}
                            </strong>
                          </Link>
                        ) : (
                          <strong>—</strong>
                        )}
                        <span
                          className={`${s.statusPill} ${
                            p.status === "confirmed"
                              ? s.statusActive
                              : p.status === "pending"
                                ? s.statusTrial
                                : s.statusSuspended
                          }`}
                        >
                          {STATUS_LABEL[p.status] || p.status}
                        </span>
                      </div>
                      <div className={s.mobileDataCardMeta}>
                        <span>{p.plan_name || "Тариф"}</span>
                        <strong className={s.payAmount}>
                          {formatRub(p.amount)} ₽
                        </strong>
                      </div>
                      <div className={s.mobileDataCardMeta}>
                        <span>
                          {p.billing_period === "yearly" ? "Год" : "Месяц"}
                        </span>
                        <span>
                          {new Date(
                            p.confirmed_at || p.created_at,
                          ).toLocaleString("ru-RU", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`${s.subTableWrap} ${s.desktopOnlyBlock}`}>
                  <table className={s.subTable}>
                    <thead>
                      <tr>
                        <th>Клуб</th>
                        <th>Тариф</th>
                        <th>Сумма</th>
                        <th>Период</th>
                        <th>Статус</th>
                        <th>Дата</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td>
                            {p.tenant_id ? (
                              <Link
                                to={`/admin/tenants/${p.tenant_id}`}
                                className={s.subClubLink}
                              >
                                <strong>
                                  {p.tenant_name || p.tenant_subdomain || "Клуб"}
                                </strong>
                                <small>{p.tenant_subdomain}</small>
                              </Link>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{p.plan_name || "—"}</td>
                          <td>{formatRub(p.amount)} ₽</td>
                          <td>
                            {p.billing_period === "yearly" ? "Год" : "Месяц"}
                          </td>
                          <td>
                            <span
                              className={`${s.statusPill} ${
                                p.status === "confirmed"
                                  ? s.statusActive
                                  : p.status === "pending"
                                    ? s.statusTrial
                                    : s.statusSuspended
                              }`}
                            >
                              {STATUS_LABEL[p.status] || p.status}
                            </span>
                          </td>
                          <td>
                            {new Date(
                              p.confirmed_at || p.created_at,
                            ).toLocaleString("ru-RU", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
